<template>
  <div class="h-full flex pb-6 justify-center">
    <Bar
      v-show="weeklyPerformanceGraphAction.isNot(States.LOADING)"
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />

    <AppSpinner
      v-if="weeklyPerformanceGraphAction.is(States.LOADING)"
      class="my-auto"
    />
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { useProjectsStore } from '@/store/projects';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { computed, ref, watch } from 'vue';
import { Bar } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  projectId: number;
  selectedDate: string;
}>();

const projectsStore = useProjectsStore();
const weeklyPerformanceGraphAction = useStoreApiAction(projectsStore.weeklyPerformanceGraph);
// Chart configuration
const chartId = 'weekly-performance-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;
const metrics = ref([]);

const chartData = computed(() => {
  const labels = [];
  const costs = [];
  const earned = [];
  const variance = [];

  metrics.value.forEach((metric) => {
    labels.push(new Date(metric.label).toLocaleDateString());
    costs.push(metric.cost);
    earned.push(metric.earned);
    variance.push(metric.variance);
  });

  return {
    labels,
    datasets: [{
      label: 'Weekly Cost',
      data: costs,
      backgroundColor: '#19428d',
      borderColor: '#19428d',
      borderWidth: 1,
      stack: 'Stack 0',
    }, {
      label: 'Weekly Earned',
      data: earned,
      backgroundColor: '#ffa010',
      borderColor: '#ffa010',
      borderWidth: 1,
      stack: 'Stack 1',
    }, {
      label: 'Variance',
      data: variance,
      backgroundColor: '#707070',
      borderColor: '#707070',
      borderWidth: 1,
      stack: 'Stack 2',
    }],
  };
});

const chartOptions = {
  plugins: {
    title: {
      display: false,
    },

    legend: {
      display: true,
    },
  },

  interaction: {
    intersect: false,
  },

  scales: {
    x: {
      stacked: true,
    },
  },
};

watch(() => props.projectId, (newValue) => {
  if(newValue !== undefined) {
    weeklyPerformanceGraphAction.request(props.projectId).then((data) => {
      metrics.value = data;
    });
  }
}, { immediate: true });
</script>

<style scoped></style>
